import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trackEvent } from '../../utils/analytics';
import { Settings, Plus, X, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { SectionTab, SectionTabs } from '../ui/section-tabs';
import { SwipeRoot } from '../ui/swipe-actions';
import { Skeleton } from '../ui/skeleton';
import { Button } from '../ui/button';
import { useToast } from '../ui/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { climbsService, type ClimbsSearchParams } from '../../services/climbs';
import { climbsListCache } from '../../services/climbsListCache';
import { climbScansService } from '../../services/climb-scans';
import { isNewClimb } from '../../services/gymVisits';
import { useClimbFilters, useFilteredClimbsList } from '../../hooks/useClimbFilters';
import { useAuth } from '../../hooks/useAuth';
import { DirectUploadProvider } from '@docflow/react-activestorage-provider';
import { apiClient } from '../../utils/api-client';
import { tokenStorage } from '../../utils/api-client';
import type { Climb } from '../../types/climb';
import type { Board } from '../../types/device';
import ClimbRow from './ClimbRow';
import ClimbAdminActions from './ClimbAdminActions';
import { isClimbAuthor } from './useClimbAdminActions';
import { ClimbImageLightbox } from './ClimbImageLightbox';
import ClimbsFilterPanel from './ClimbsFilterPanel';
import ClimbProgressBar from './ClimbProgressBar';
import { ClimbsListFab } from './ClimbsListFab';
import SchedulePanel from '../schedule/SchedulePanel';
import BoardTabContent from './BoardTabContent';
import type { RouteSetter } from '../../types/route-setter';

function getInitialCachedClimbs(filterKey: string): Climb[] {
  if (!filterKey) return [];
  if (!sessionStorage.getItem(`last_viewed_climb_${filterKey}`)) return [];
  try {
    const stored = sessionStorage.getItem(`filteredClimbs_${filterKey}`);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? (parsed as Climb[]) : [];
  } catch {
    return [];
  }
}

/**
 * Coming back to the tab within this long is the same visit: nothing on a
 * wall changes that fast, and every glance at another app would otherwise
 * cost a request on the way back.
 */
const REVALIDATE_AFTER_MS = 60_000;

interface ListRequest {
  context: 'gym' | 'user-profile';
  createdBySlug?: string;
  selectedLocation: string;
  locationId?: string | number;
  gymSlug?: string;
}

/** What the list asks the API for. */
function listRequestParams({ context, createdBySlug, selectedLocation, locationId, gymSlug }: ListRequest): ClimbsSearchParams {
  const params: ClimbsSearchParams = { include_sends: true, include_user_attempts: true };

  if (context === 'user-profile' && createdBySlug) {
    params.created_by = createdBySlug;
    if (selectedLocation !== 'all') params.location_id = selectedLocation;
  } else if (gymSlug) {
    // By slug, which the page has before it has the gym: that is what lets it
    // send this request off together with the gym's own (see GymProfile).
    params.location_slug = gymSlug;
  } else if (locationId) {
    params.location_id = locationId;
  }

  return params;
}

/**
 * What the stored copy of the list is filed under: the list, and who is
 * reading it — the rows carry the reader's own standing on each climb.
 */
function listCacheKey(request: ListRequest, readerId: string | number | undefined): string {
  const list = request.context === 'user-profile'
    ? `user:${request.createdBySlug}:${request.selectedLocation}`
    : `gym:${request.gymSlug ?? request.locationId}`;
  return `${readerId ?? 'anon'}:${list}`;
}

interface ClimbsListProps {
  locationId?: string | number;
  gymSlug?: string;
  locationType?: 'gym' | 'competition'; // Determines URL path (gyms vs competitions)
  context?: 'gym' | 'user-profile';
  createdBySlug?: string; // For user-profile context
  userGyms?: Array<{ id: string | number; name: string; slug: string }>; // For user-profile context
  returnPath?: string; // Path to return when clicking back
  onFilteredClimbsChange?: (climbs: Climb[]) => void;
  onGroupByChange?: (groupBy: 'none' | 'setter' | 'grade' | 'zone') => void;
  onAddAnnouncement?: () => void; // Staff-only FAB action to create a gym announcement
  onTabSelect?: () => void; // Fired when a section tab is tapped (used to collapse the page header on mobile)
  boards?: Board[]; // The gym's boards, each rendered as its own tab (behind the :boards flag)
  /**
   * What "new" is measured against on this visit, from
   * `gymVisitsService.recordVisit`: the last look on an earlier day, or null on
   * a first visit, when only today's climbs are new. Left out by a page that
   * keeps no visits (a profile), and no row wears the tag.
   */
  newSince?: Date | null;
}


export default function ClimbsList({
  locationId,
  gymSlug,
  locationType = 'gym',
  context = 'gym',
  createdBySlug,
  userGyms = [],
  returnPath,
  onFilteredClimbsChange,
  onGroupByChange,
  onAddAnnouncement,
  onTabSelect,
  boards = [],
  newSince,
}: ClimbsListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Whether a row wears the "New" tag: only where the page told us about the
  // visit at all.
  const isNew = (climb: Climb) => newSince !== undefined && isNewClimb(climb.created_at, newSince);
  const filterKey = context === 'user-profile' ? `user-${createdBySlug}` : locationId?.toString() || '';
  const [searchParams, setSearchParams] = useSearchParams();
  const { filters, updateFilters } = useClimbFilters(filterKey, { searchParams, setSearchParams });
  const { saveFilteredClimbs } = useFilteredClimbsList(filterKey);

  const listRequest: ListRequest = {
    context,
    createdBySlug,
    selectedLocation: filters.selectedLocation || 'all',
    locationId,
    gymSlug,
  };
  const cacheKey = listCacheKey(listRequest, user?.id);

  // Whether the next fetch refreshes rows already on screen — from the stored
  // copy of the list, or from the filtered list the reader is coming back to
  // — or is the first anyone sees of them, behind a skeleton.
  const isBackgroundRefetchRef = useRef(false);
  const [climbs, setClimbs] = useState<Climb[]>(() => {
    const initial = climbsListCache.read(cacheKey) ?? getInitialCachedClimbs(filterKey);
    if (initial.length > 0) isBackgroundRefetchRef.current = true;
    return initial;
  });
  const [loading, setLoading] = useState(!isBackgroundRefetchRef.current);
  const [error, setError] = useState<string | null>(null);
  const [fetchRefreshKey, setFetchRefreshKey] = useState(0);
  // How the next fetch treats the browser's HTTP cache: validated against the
  // server as a rule, which answers 304 and no body when nothing changed; a
  // `reload` only after a change this client made itself.
  const fetchCacheModeRef = useRef<RequestCache>('no-cache');
  const lastFetchedAtRef = useRef(0);
  // The key whose stored copy the rows on screen came from, so a change of
  // list (the profile's gym filter, say) reads the new one before fetching.
  const hydratedKeyRef = useRef(cacheKey);
  const [deletingClimbIds, setDeletingClimbIds] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);

  const { toast } = useToast();

  // Check if current user is staff admin for this gym
  const currentUserStaff = user?.staff?.find(staff => staff.gym_slug === gymSlug) || null;
  const isStaffAdmin = currentUserStaff?.staff_roles.includes('admin') && currentUserStaff?.status === 'confirmed';
  const isStaffMember = currentUserStaff?.status === 'confirmed' &&
    (currentUserStaff?.staff_roles.includes('admin') || currentUserStaff?.staff_roles.includes('editor'));

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Highlight + scroll to the climb the user navigated back from
  const [highlightedClimbSlug, setHighlightedClimbSlug] = useState<string | null>(null);

  useEffect(() => {
    const key = `last_viewed_climb_${filterKey}`;
    const slug = sessionStorage.getItem(key);
    if (!slug) return;
    sessionStorage.removeItem(key);
    setHighlightedClimbSlug(slug);
  }, [filterKey]);

  const scrollToHighlighted = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Start the fade-out timer only after the element is visible
      setTimeout(() => setHighlightedClimbSlug(null), 2000);
    }
  }, []);

  // The rows' handlers read the list on screen through this ref rather than
  // closing over it, so they keep their identity from one render to the next
  // and a memoized ClimbRow is not redrawn — two hundred of them — every time
  // a filter, a highlight or a fade-out changes something above them.
  const currentFilteredClimbsRef = useRef<Climb[]>([]);

  const handleLongPress = useCallback((climb: Climb) => {
    const idx = currentFilteredClimbsRef.current.findIndex((c) => c.id === climb.id);
    setLightboxIndex(idx >= 0 ? idx : 0);
  }, []);

  // Scan upload state
  const [showScanDialog, setShowScanDialog] = useState(false);
  // The photo has gone up and nothing is happening yet, which is the whole
  // point: uploading no longer starts the analysis, so the only honest thing to
  // show is that it arrived and that a notification is coming.
  const [showScanSent, setShowScanSent] = useState(false);
  const [scanUploading, setScanUploading] = useState(false);
  const scanCameraInputRef = useRef<HTMLInputElement>(null);
  const scanGalleryInputRef = useRef<HTMLInputElement>(null);
  const scanHandleUploadRef = useRef<((files: File[]) => void) | undefined>(undefined);

  function getApiOrigin() {
    const baseURL = apiClient.getBaseURL();
    if (!baseURL) {
      return { host: window.location.hostname, port: window.location.port || undefined, protocol: window.location.protocol.replace(':', '') };
    }
    try {
      const url = new URL(baseURL);
      return { host: url.hostname, port: url.port || undefined, protocol: url.protocol.replace(':', '') };
    } catch {
      return { host: window.location.hostname, port: window.location.port || undefined, protocol: window.location.protocol.replace(':', '') };
    }
  }

  async function handleScanUploadSuccess(signedIds: string[]) {
    if (!signedIds.length || !gymSlug) return;
    const climbType = activeTab === 'boulders' ? 'boulder' : 'route';
    try {
      await climbScansService.createScan(gymSlug, {
        climb_type: climbType,
        image_signed_id: signedIds[0],
      });
      setShowScanDialog(false);
      // No walking the setter to the scan's page: there is nothing on it to
      // see. The analysis runs on the hourly batch and the gym's staff are
      // emailed and pushed when it lands (ClimbScanSubscriber), so the honest
      // answer here is "it arrived, we will tell you".
      setShowScanSent(true);
    } catch {
      toast({ title: t('climbScan.uploadError'), variant: 'destructive' });
    } finally {
      setScanUploading(false);
    }
  }

  function handleScanFileSelected(file: File) {
    if (scanHandleUploadRef.current) {
      setScanUploading(true);
      scanHandleUploadRef.current([file]);
    }
  }

  const [bulkDiscardLoading, setBulkDiscardLoading] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Use the filters directly from the hook, no local state
  const activeTab = filters.activeTab;
  const gradeRange = filters.gradeRange;
  const selectedRouteSetter = filters.selectedRouteSetter;
  const selectedHoldColor = filters.selectedHoldColor;
  const selectedZone = filters.selectedZone || 'all';
  const selectedLocation = filters.selectedLocation || 'all';
  const showUnofficial = filters.showUnofficial;
  const showOnlyNotSent = filters.showOnlyNotSent;
  const sortBy = filters.sortBy;
  const groupBy = filters.groupBy;
  const olderThanDays = filters.olderThanDays || '';

  // The "schedule" (Palinsesto) tab has no climbs — it's a distinct section.
  const isSchedule = activeTab === 'schedule';
  // A board tab is likewise not a climb list: for now it shows a placeholder,
  // later the climbs linked to that board.
  const isBoard = activeTab === 'board';
  const selectedBoard = isBoard ? boards.find(b => b.id === filters.boardId) : undefined;
  // Neither the schedule nor a board tab renders climb-specific UI (filters,
  // progress, FAB, add/scan) — they are distinct, climb-less sections.
  const isClimbList = !isSchedule && !isBoard;
  // Concrete climb type used by climb-specific UI (add/scan/progress), which is
  // never rendered while the schedule or a board tab is active.
  const climbTypeTab: 'boulders' | 'routes' = activeTab === 'routes' ? 'routes' : 'boulders';

  // A board tab whose board no longer exists (e.g. it was removed, or a stale
  // deep link) falls back to the default boulders tab.
  useEffect(() => {
    if (isBoard && !selectedBoard) {
      updateFilters({ activeTab: 'boulders', boardId: undefined });
    }
  }, [isBoard, selectedBoard, updateFilters]);

  // The tab strip scrolls horizontally once the tabs (boulders, routes,
  // schedule and one per board) no longer fit, so bring the selected one into
  // view — otherwise a deep-linked board tab starts off-screen.
  const tabsStripRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const strip = tabsStripRef.current;
    const active = strip?.querySelector<HTMLElement>('[data-tab-active="true"]');
    if (!strip || !active || !strip.scrollTo) return;
    const left = active.offsetLeft - (strip.clientWidth - active.clientWidth) / 2;
    strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [activeTab, filters.boardId]);

  const selectedLocationFilter = filters.selectedLocation || 'all';
  useEffect(() => {
    let cancelled = false;
    const request: ListRequest = { context, createdBySlug, selectedLocation: selectedLocationFilter, locationId, gymSlug };
    const key = listCacheKey(request, user?.id);

    // A different list than the rows on screen came from: show its stored
    // copy, if there is one, while the fresh one is fetched. The first list's
    // copy went straight into the initial state.
    if (hydratedKeyRef.current !== key) {
      hydratedKeyRef.current = key;
      const cached = climbsListCache.read(key);
      if (cached) {
        setClimbs(cached);
        isBackgroundRefetchRef.current = true;
      }
    }

    const background = isBackgroundRefetchRef.current;
    isBackgroundRefetchRef.current = false;
    const cacheMode = fetchCacheModeRef.current;
    fetchCacheModeRef.current = 'no-cache';

    const fetchClimbs = async () => {
      try {
        if (!background) setLoading(true);
        setError(null);
        const data = await climbsService.searchClimbs(listRequestParams(request), { cache: cacheMode });
        if (cancelled) return;
        setClimbs(data);
        lastFetchedAtRef.current = Date.now();
        climbsListCache.write(key, data);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch climbs:', err);
        // Rows already on screen are worth more than an error card: a refresh
        // that failed (offline, say) leaves the last known wall up.
        if (!background) setError(err instanceof Error ? err.message : 'Failed to fetch climbs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchClimbs();
    return () => {
      cancelled = true;
    };
  }, [locationId, gymSlug, createdBySlug, context, selectedLocationFilter, user?.id, fetchRefreshKey]);

  // Refetch climbs in the background when the page regains visibility (e.g.
  // navigating back) — unless the rows are a minute old at most, in which
  // case they are as good as fresh.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFetchedAtRef.current < REVALIDATE_AFTER_MS) return;
      isBackgroundRefetchRef.current = true;
      setFetchRefreshKey(k => k + 1);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Memoize climb types availability
  const climbsInfo = useMemo(() => {
    const hasClimbs = climbs.length > 0;
    const hasBoulders = hasClimbs && climbs.some(climb => climb.type === 'Boulder');
    const hasRoutes = hasClimbs && climbs.some(climb => climb.type === 'Route');
    return { hasClimbs, hasBoulders, hasRoutes };
  }, [climbs]);

  // Set the active tab based on available data
  useEffect(() => {
    if (!climbsInfo.hasClimbs) return;

    if (activeTab === 'boulders' && !climbsInfo.hasBoulders && climbsInfo.hasRoutes) {
      updateFilters({ activeTab: 'routes' });
    } else if (activeTab === 'routes' && !climbsInfo.hasRoutes && climbsInfo.hasBoulders) {
      updateFilters({ activeTab: 'boulders' });
    }
  }, [climbsInfo, activeTab, updateFilters]);

  // Separate boulders and routes
  const boulders = useMemo(() =>
    climbs.filter(climb => climb.type === 'Boulder'),
    [climbs]
  );

  const routes = useMemo(() =>
    climbs.filter(climb => climb.type === 'Route'),
    [climbs]
  );

  // Helper: compute how many days ago a date is
  const getDaysAgo = useCallback((dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    return Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  }, []);

  // Get filter options based on the active tab (must be before filterClimbs)
  const currentClimbs = useMemo(() => {
    if (activeTab === 'schedule' || activeTab === 'board') return [];
    return activeTab === 'boulders' ? boulders : routes;
  }, [activeTab, boulders, routes]);

  // The progress bar (which now hosts the filter button) only renders for logged-in
  // users when the tab has confirmed, graded climbs — mirror that here so we know
  // whether the filter button lives in the progress bar or needs its own row.
  const hasGradedClimbs = useMemo(
    () => currentClimbs.some(climb => climb.difficulty && climb.official_status === 'confirmed'),
    [currentClimbs]
  );
  const progressBarVisible = !!user && hasGradedClimbs;

  // The "open filters" button. It lives inside the progress bar when that is shown,
  // otherwise it falls back to its own row. Only one instance renders at a time.
  const filterButton = (
    <button
      data-tour="gym-filter-button"
      data-testid="gym-filter-button"
      onClick={() => setShowFilters(true)}
      className="flex items-center justify-center w-8 h-8 rounded-md border hover:bg-muted transition-colors"
    >
      <Settings className="w-4 h-4" />
    </button>
  );

  // Grade options sorted by difficulty_value (ascending) - must be before filterClimbs
  const gradeOptions = useMemo(() => {
    const seen = new Map<string, number | null>();
    currentClimbs.forEach(climb => {
      if (climb.difficulty && !seen.has(climb.difficulty)) {
        seen.set(climb.difficulty, climb.difficulty_value ?? null);
      }
    });
    return [...seen.entries()]
      .sort((a, b) => {
        const va = a[1] ?? Infinity;
        const vb = b[1] ?? Infinity;
        return va - vb;
      })
      .map(([grade]) => grade);
  }, [currentClimbs]);

  // Function to filter climbs.
  //
  // `ignoreZone` leaves the sector out of the reckoning, which is what the strip
  // of sectors is built from: it has to go on offering the sectors the rest of
  // the filters leave standing, including the one already picked — reading it
  // off the fully filtered list would leave the strip with one tab on it.
  const filterClimbs = useCallback((climbsList: Climb[], { ignoreZone = false } = {}) => {
    // Pre-compute the set of valid grades from the range
    let validGrades: Set<string> | null = null;
    if (gradeRange) {
      const loIdx = gradeOptions.indexOf(gradeRange[0]);
      const hiIdx = gradeOptions.indexOf(gradeRange[1]);
      if (loIdx !== -1 && hiIdx !== -1) {
        validGrades = new Set(gradeOptions.slice(loIdx, hiIdx + 1));
      }
    }

    return climbsList.filter(climb => {
      const gradeMatch = !validGrades ||
        (climb.difficulty != null && validGrades.has(climb.difficulty));
      // Keyed on the setter id, not the slug: a guest setter has no profile
      // and so no slug, and they still need to be filterable.
      const routeSetterMatch = selectedRouteSetter === 'all' ||
        climb.route_setter?.id === selectedRouteSetter;

      // If showUnofficial is false, only show confirmed climbs
      // If showUnofficial is true, show all climbs
      const statusMatch = showUnofficial || climb.official_status === 'confirmed';

      // If showOnlyNotSent is true, only show climbs the current user has not topped (via ascent_log)
      const sentMatch = !showOnlyNotSent || !climb.user_topped;

      const holdColorMatch = selectedHoldColor === 'all' ||
        climb.hold_color?.toLowerCase() === selectedHoldColor?.toLowerCase();

      const zoneMatch = ignoreZone || selectedZone === 'all' || climb.zone?.id === selectedZone;

      // Staff-only: filter by age in days
      const olderThanDaysMatch = !olderThanDays ||
        getDaysAgo(climb.created_at) >= Number(olderThanDays);

      return gradeMatch && routeSetterMatch && statusMatch && sentMatch && holdColorMatch && zoneMatch && olderThanDaysMatch;
    });
  }, [gradeRange, gradeOptions, selectedRouteSetter, showUnofficial, showOnlyNotSent, selectedHoldColor, selectedZone, olderThanDays, getDaysAgo]);

  // Function to sort climbs
  const sortClimbs = useCallback((climbsList: Climb[]) => {
    const sorted = [...climbsList];

    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      // Within a grade, the crowd's opinion breaks the tie; a climb nobody has
      // voted on sits at "as graded" (.5) — between the soft ones and the hard
      // ones, not below both.
      case 'easiest':
        return sorted.sort((a, b) => {
          const valA = a.difficulty_value ?? Infinity;
          const valB = b.difficulty_value ?? Infinity;
          if (valA !== valB) return valA - valB;
          const opA = a.average_grade_opinion ?? 0.5;
          const opB = b.average_grade_opinion ?? 0.5;
          return opA - opB;
        });
      case 'hardest':
        return sorted.sort((a, b) => {
          const valA = a.difficulty_value ?? -Infinity;
          const valB = b.difficulty_value ?? -Infinity;
          if (valA !== valB) return valB - valA;
          const opA = a.average_grade_opinion ?? 0.5;
          const opB = b.average_grade_opinion ?? 0.5;
          return opB - opA;
        });
      default:
        return sorted;
    }
  }, [sortBy]);

  const filteredBoulders = useMemo(() => sortClimbs(filterClimbs(boulders)), [boulders, filterClimbs, sortClimbs]);
  const filteredRoutes = useMemo(() => sortClimbs(filterClimbs(routes)), [routes, filterClimbs, sortClimbs]);

  // Get filtered climbs for the current tab
  const currentFilteredClimbs = useMemo(() => {
    if (activeTab === 'schedule' || activeTab === 'board') return [];
    return activeTab === 'boulders' ? filteredBoulders : filteredRoutes;
  }, [activeTab, filteredBoulders, filteredRoutes]);
  useEffect(() => {
    currentFilteredClimbsRef.current = currentFilteredClimbs;
  }, [currentFilteredClimbs]);

  // Group climbs by selected groupBy field
  const groupedClimbs = useMemo(() => {
    if (groupBy === 'none') return null;

    const groups = new Map<string, { label: string; sortKey: number; climbs: Climb[] }>();

    for (const climb of currentFilteredClimbs) {
      let key: string;
      let label: string;
      let sortKey: number;

      if (groupBy === 'setter') {
        key = climb.route_setter?.id || '__none__';
        label = climb.route_setter?.name || t('climbs.groupNoSetter');
        sortKey = 0;
      } else if (groupBy === 'zone') {
        key = climb.zone?.id?.toString() || '__none__';
        label = climb.zone?.name || t('climbs.groupNoZone');
        sortKey = 0;
      } else {
        key = climb.difficulty || '__none__';
        label = climb.difficulty || t('climbs.groupNoGrade');
        sortKey = climb.difficulty_value ?? Infinity;
      }

      if (!groups.has(key)) {
        groups.set(key, { label, sortKey, climbs: [] });
      }
      groups.get(key)!.climbs.push(climb);
    }

    const sorted = [...groups.values()].sort((a, b) =>
      groupBy === 'grade' ? a.sortKey - b.sortKey : a.label.localeCompare(b.label)
    );

    return sorted;
  }, [currentFilteredClimbs, groupBy, t]);

  // Compute available "days ago" options from current tab climbs (staff-only filter)
  const daysAgoOptions = useMemo(() => {
    const daysSet = new Set<number>();
    currentClimbs.forEach(climb => {
      const days = getDaysAgo(climb.created_at);
      if (days > 0) daysSet.add(days);
    });
    return [...daysSet].sort((a, b) => a - b);
  }, [currentClimbs, getDaysAgo]);


  const holdColorOptions = useMemo(() => {
    const colors = [...new Set(currentClimbs.map(climb => climb.hold_color).filter((c): c is string => !!c))];
    return colors.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [currentClimbs]);

  // The sectors worth offering: the ones that still have a climb in them once
  // everything else the reader asked for is applied. A sector with nothing left
  // in it is a tab that answers with an empty list.
  const zoneOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const climb of filterClimbs(currentClimbs, { ignoreZone: true })) {
      if (climb.zone?.id && !seen.has(climb.zone.id)) seen.set(climb.zone.id, climb.zone.name);
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [currentClimbs, filterClimbs]);

  // Whether anything is narrowing the list right now. It is what brings the
  // sector strip out: once the reader has started narrowing, the sectors are the
  // next cut they are likely to want, and few enough of them are left to fit.
  const hasActiveFilters =
    !!gradeRange ||
    selectedRouteSetter !== 'all' ||
    selectedHoldColor !== 'all' ||
    showUnofficial ||
    showOnlyNotSent ||
    !!olderThanDays;

  // A handful of sectors is a strip that fits whatever the reader is doing, so
  // it is up from the start. Any more and it waits until the list is being
  // narrowed — or until one of them is picked, which must not hide the control
  // that picked it.
  const showZoneStrip =
    zoneOptions.length > 0 && (zoneOptions.length <= 3 || hasActiveFilters || selectedZone !== 'all');

  const routeSetterOptions = useMemo(() => {
    const setters = currentClimbs
      .map(climb => climb.route_setter)
      .filter((setter): setter is RouteSetter => setter !== null && setter !== undefined)
      .filter((setter, index, self) =>
        self.findIndex(s => s.id === setter.id) === index
      );
    return setters;
  }, [currentClimbs]);

  // Save the filtered list in session storage when it changes
  useEffect(() => {
    if (currentFilteredClimbs.length > 0) {
      saveFilteredClimbs(currentFilteredClimbs);
    }
  }, [currentFilteredClimbs, saveFilteredClimbs]);

  // Notify parent when filtered climbs change (staff stats)
  useEffect(() => {
    if (onFilteredClimbsChange) {
      onFilteredClimbsChange(currentFilteredClimbs);
    }
  }, [currentFilteredClimbs, onFilteredClimbsChange]);

  // Notify parent when groupBy changes (staff stats)
  useEffect(() => {
    if (onGroupByChange) {
      onGroupByChange(groupBy);
    }
  }, [groupBy, onGroupByChange]);

  // Handler for clicking on a climb
  const handleClimbClick = useCallback((climb: Climb) => {
    const list = currentFilteredClimbsRef.current;
    const currentIndex = list.findIndex(c => c.id === climb.id);
    saveFilteredClimbs(list, currentIndex);

    // Save return path if provided
    if (returnPath) {
      sessionStorage.setItem(`climb_return_path_${climb.slug}`, returnPath);
    }

    // Save slug so the list can highlight it when the user navigates back
    sessionStorage.setItem(`last_viewed_climb_${filterKey}`, climb.slug);

    // Determine the URL path based on location type. Board climbs are
    // boulder-style, so they ride the boulders segment (the climb is fetched by
    // slug regardless).
    const locationPath = locationType === 'competition' ? 'competitions' : 'gyms';
    const climbType = climb.type === 'Route' ? 'routes' : 'boulders';
    if (climb.location?.slug) {
      navigate(`/${locationPath}/${climb.location.slug}/${climbType}/${climb.slug}`, { state: { climb } });
    } else if (gymSlug) {
      navigate(`/${locationPath}/${gymSlug}/${climbType}/${climb.slug}`, { state: { climb } });
    }
  }, [saveFilteredClimbs, returnPath, filterKey, locationType, gymSlug, navigate]);

  // Handler for when a climb is updated (admin actions)
  const handleClimbUpdated = useCallback((updatedClimb: Climb) => {
    setClimbs(prevClimbs =>
      prevClimbs.map(climb =>
        climb.id === updatedClimb.id ? updatedClimb : climb
      )
    );
  }, []);

  // Handler for when a climb is deleted (admin actions)
  const handleClimbDeleted = useCallback((climbId: string) => {
    // Immediately update sessionStorage (before animation)
    saveFilteredClimbs(currentFilteredClimbsRef.current.filter(climb => climb.id !== climbId));
    // Trigger fade-out animation
    setDeletingClimbIds(prev => new Set([...prev, climbId]));
    // After animation, remove from state and re-fetch in background. This
    // client changed the wall itself, so the browser's copy is bypassed
    // outright rather than validated.
    setTimeout(() => {
      setClimbs(prevClimbs => prevClimbs.filter(climb => climb.id !== climbId));
      setDeletingClimbIds(prev => { const next = new Set(prev); next.delete(climbId); return next; });
      isBackgroundRefetchRef.current = true;
      fetchCacheModeRef.current = 'reload';
      setFetchRefreshKey(k => k + 1);
    }, 300);
  }, [saveFilteredClimbs]);

  // Handler for bulk discard (staff only)
  const handleBulkDiscard = async () => {
    if (!locationId || !olderThanDays) return;

    setBulkDiscardLoading(true);
    const days = Number(olderThanDays);
    try {
      const result = await climbsService.bulkDiscard(locationId, days);
      // Remove discarded climbs from local state
      setClimbs(prevClimbs =>
        prevClimbs.filter(climb => getDaysAgo(climb.created_at) < days)
      );
      toast({
        title: t('climbs.admin.bulkDiscardSuccess'),
        description: t('climbs.admin.bulkDiscardSuccessDescription', { count: result.discarded_count }),
      });
      updateFilters({ olderThanDays: undefined });
    } catch (error) {
      console.error('Failed to bulk discard:', error);
      toast({
        title: t('climbs.admin.bulkDiscardError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setBulkDiscardLoading(false);
      setShowDiscardConfirm(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="animate-pulse bg-muted w-16 h-16 rounded-md" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="animate-pulse bg-muted h-4 w-48" />
                  <Skeleton className="animate-pulse bg-muted h-3 w-32" />
                </div>
                <div className="flex -space-x-2">
                  <Skeleton className="animate-pulse bg-muted w-8 h-8 rounded-full" />
                  <Skeleton className="animate-pulse bg-muted w-8 h-8 rounded-full" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (climbs.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-muted-foreground mb-6">{t('climbs.noClimbsInGym')}</p>
          {context === 'gym' && gymSlug && (
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <button
                onClick={() => {
                  trackEvent('climb_create_started', { climb_type: 'boulders' });
                  navigate(`/${locationType === 'competition' ? 'competitions' : 'gyms'}/${gymSlug}/boulders/new`);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                <Plus className="w-4 h-4" />
                {t('climbs.createFirstBoulder')}
              </button>
              <button
                onClick={() => {
                  trackEvent('climb_create_started', { climb_type: 'routes' });
                  navigate(`/${locationType === 'competition' ? 'competitions' : 'gyms'}/${gymSlug}/routes/new`);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                <Plus className="w-4 h-4" />
                {t('climbs.createFirstRoute')}
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    // The gaps between the blocks of this page — the tab strip, the progress
    // bar, the rows — kept tight: they are one panel read top to bottom, not
    // separate cards that need air between them.
    <div className="space-y-3">
      <div className="space-y-2">
        {/* Static section tabs: boulders / routes / schedule (Palinsesto).
            The strip is the one every page's sections are read through (see
            SectionTabs); it scrolls horizontally instead of overflowing the
            viewport. */}
        <SectionTabs ref={tabsStripRef}>
          {boulders.length > 0 && (
            <SectionTab
              active={activeTab === 'boulders'}
              onClick={() => {
                updateFilters({
                  activeTab: 'boulders',
                  gradeRange: null,
                  selectedRouteSetter: 'all',
                  selectedHoldColor: 'all',
                });
                onTabSelect?.();
              }}
            >
              {t('climbs.boulders')} ({boulders.length})
            </SectionTab>
          )}
          {routes.length > 0 && (
            <SectionTab
              active={activeTab === 'routes'}
              onClick={() => {
                updateFilters({
                  activeTab: 'routes',
                  gradeRange: null,
                  selectedRouteSetter: 'all',
                  selectedHoldColor: 'all',
                });
                onTabSelect?.();
              }}
            >
              {t('climbs.routes')} ({routes.length})
            </SectionTab>
          )}
          {context === 'gym' && (
            <SectionTab
              active={isSchedule}
              onClick={() => {
                updateFilters({ activeTab: 'schedule' });
                onTabSelect?.();
              }}
            >
              {t('climbs.schedule')}
            </SectionTab>
          )}
          {/* One tab per board (behind the :boards flag — only populated when the
              gym passes boards). Clicking opens the board's placeholder for now. */}
          {context === 'gym' && boards.map((board) => (
            <SectionTab
              key={board.id}
              active={isBoard && filters.boardId === board.id}
              onClick={() => {
                updateFilters({ activeTab: 'board', boardId: board.id });
                onTabSelect?.();
              }}
            >
              {board.name}
            </SectionTab>
          ))}
        </SectionTabs>

        {/* Filter button lives in the progress bar when shown; otherwise it gets its own row */}
        {isClimbList && !progressBarVisible && (
          <div className="flex items-center justify-end gap-1">
            {filterButton}
          </div>
        )}

        {/* Scan upload dialog */}
        <Dialog open={showScanDialog} onOpenChange={setShowScanDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('climbScan.uploadButton')}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-3 py-2">
              <input
                ref={scanCameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleScanFileSelected(f); }}
              />
              <input
                ref={scanGalleryInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleScanFileSelected(f); }}
              />
              <DirectUploadProvider
                origin={getApiOrigin()}
                headers={{ 'Authorization': `Bearer ${tokenStorage.getToken() || ''}` }}
                onSuccess={handleScanUploadSuccess}
                render={({ handleUpload }) => {
                  scanHandleUploadRef.current = handleUpload;
                  return null;
                }}
              />
              <Button
                variant="outline"
                className="w-full py-6 text-base"
                disabled={scanUploading}
                onClick={() => scanCameraInputRef.current?.click()}
              >
                {t('climbScan.cameraOption')}
              </Button>
              <Button
                variant="outline"
                className="w-full py-6 text-base"
                disabled={scanUploading}
                onClick={() => scanGalleryInputRef.current?.click()}
              >
                {t('climbScan.galleryOption')}
              </Button>
              {scanUploading && (
                <p className="text-center text-sm text-muted-foreground">{t('common.loading')}</p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* The photo arrived, and that is all there is to say. The wall is
            looked at on the hourly batch rather than on the upload, so there is
            no page to go to yet — only a notification to wait for. */}
        <Dialog open={showScanSent} onOpenChange={setShowScanSent}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('climbScan.sentTitle')}</DialogTitle>
            </DialogHeader>
            <p className="py-2 text-sm text-muted-foreground">{t('climbScan.sentMessage')}</p>
            <DialogFooter>
              <Button onClick={() => setShowScanSent(false)}>{t('common.ok')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {user && currentClimbs.length > 0 && (
          <ClimbProgressBar
            climbs={currentClimbs}
            climbType={climbTypeTab}
            activeGrade={gradeRange?.[0] === gradeRange?.[1] ? gradeRange?.[0] : null}
            onGradeClick={(grade) => {
              const isActive = gradeRange?.[0] === grade && gradeRange?.[1] === grade;
              updateFilters({ gradeRange: isActive ? null : [grade, grade] });
            }}
            zones={showZoneStrip ? zoneOptions : []}
            activeZone={selectedZone}
            onZoneChange={(zone) => updateFilters({ selectedZone: zone })}
            headerAction={filterButton}
          />
        )}

        {/* Active filters badges — climb tabs only (schedule and boards have no filters) */}
        {isClimbList && (() => {
          const activeFilters: Array<{ key: string; label: string; value: string }> = [];

          if (gradeRange) {
            activeFilters.push({
              key: 'grade',
              label: t('climbs.grade'),
              value: gradeRange[0] === gradeRange[1] ? gradeRange[0] : `${gradeRange[0]} – ${gradeRange[1]}`
            });
          }
          if (selectedRouteSetter !== 'all' && context === 'gym') {
            const setter = routeSetterOptions.find(s => s.id === selectedRouteSetter);
            if (setter) {
              activeFilters.push({
                key: 'setter',
                label: t('climbs.routeSetter'),
                value: setter.name
              });
            }
          }
          if (selectedHoldColor !== 'all') {
            activeFilters.push({ key: 'holdColor', label: t('climbs.holdColor'), value: selectedHoldColor });
          }
          if (selectedZone !== 'all') {
            const zone = zoneOptions.find(z => z.id === selectedZone);
            if (zone) {
              activeFilters.push({ key: 'zone', label: t('climbs.zone'), value: zone.name });
            }
          }
          if (selectedLocation !== 'all' && context === 'user-profile') {
            const gym = userGyms.find(g => g.id.toString() === selectedLocation);
            if (gym) {
              activeFilters.push({ key: 'location', label: t('climbs.filterByGym'), value: gym.name });
            }
          }
          if (showUnofficial && context === 'gym') {
            activeFilters.push({ key: 'unofficial', label: t('climbs.showUnofficial'), value: '✓' });
          }
          if (showOnlyNotSent && user) {
            activeFilters.push({ key: 'notSent', label: t('climbs.showOnlyNotSent'), value: '✓' });
          }
          if (olderThanDays && isStaffMember) {
            activeFilters.push({ key: 'olderThanDays', label: t('climbs.olderThanDays'), value: t('climbs.olderThanDaysValue', { count: Number(olderThanDays) }) });
          }
          if (groupBy !== 'none' && context === 'gym') {
            const groupByLabels: Record<string, string> = {
              setter: t('climbs.groupBySetter'),
              grade: t('climbs.groupByGrade'),
              zone: t('climbs.groupByZone'),
            };
            activeFilters.push({ key: 'groupBy', label: t('climbs.groupBy'), value: groupByLabels[groupBy] });
          }

          if (activeFilters.length === 0) return null;

          return (
            <div className="flex flex-wrap items-center gap-2">
              {activeFilters.map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => {
                    // Remove the specific filter
                    if (filter.key === 'grade') updateFilters({ gradeRange: null });
                    else if (filter.key === 'setter') updateFilters({ selectedRouteSetter: 'all' });
                    else if (filter.key === 'holdColor') updateFilters({ selectedHoldColor: 'all' });
                    else if (filter.key === 'zone') updateFilters({ selectedZone: 'all' });
                    else if (filter.key === 'location') updateFilters({ selectedLocation: 'all' });
                    else if (filter.key === 'unofficial') updateFilters({ showUnofficial: false });
                    else if (filter.key === 'notSent') updateFilters({ showOnlyNotSent: false });
                    else if (filter.key === 'olderThanDays') updateFilters({ olderThanDays: undefined });
                    else if (filter.key === 'groupBy') updateFilters({ groupBy: 'none' });
                  }}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                >
                  <span className="text-muted-foreground">{filter.label}:</span>
                  <span>{filter.value}</span>
                  <X className="w-3 h-3 ml-0.5" />
                </button>
              ))}
              {activeFilters.length > 1 && (
                <button
                  onClick={() => {
                    updateFilters({
                      gradeRange: null,
                      selectedRouteSetter: 'all',
                      selectedHoldColor: 'all',
                      selectedZone: 'all',
                      selectedLocation: 'all',
                      showUnofficial: false,
                      showOnlyNotSent: false,
                      olderThanDays: undefined,
                      groupBy: 'none',
                    });
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                >
                  {t('climbs.clearAllFilters')}
                </button>
              )}
            </div>
          );
        })()}

        {/* Filters live in a scrollable dialog instead of an inline accordion */}
        <Dialog open={showFilters} onOpenChange={setShowFilters}>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('climbs.filters')}</DialogTitle>
            </DialogHeader>
            <ClimbsFilterPanel
              filters={filters}
              updateFilters={updateFilters}
              context={context}
              gradeOptions={gradeOptions}
              routeSetterOptions={routeSetterOptions}
              holdColorOptions={holdColorOptions}
              daysAgoOptions={daysAgoOptions}
              userGyms={userGyms}
              isStaffMember={!!isStaffMember}
              isAuthenticated={!!user}
            />
            <DialogFooter>
              <Button onClick={() => setShowFilters(false)}>
                {t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Staff-only: Bulk discard button when olderThanDays filter is active */}
      {isStaffMember && context === 'gym' && olderThanDays && currentFilteredClimbs.length > 0 && (
        <div className="space-y-2">
          {!showDiscardConfirm ? (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowDiscardConfirm(true)}
              disabled={bulkDiscardLoading}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t('climbs.admin.bulkDiscard', { count: currentFilteredClimbs.length })}
            </Button>
          ) : (
            <div className="flex items-center gap-3 p-3 border border-destructive/50 rounded-lg bg-destructive/5">
              <p className="text-sm text-destructive font-medium flex-1">
                {t('climbs.admin.bulkDiscardConfirm', { count: currentFilteredClimbs.length, days: olderThanDays })}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDiscardConfirm(false)}
                  disabled={bulkDiscardLoading}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDiscard}
                  disabled={bulkDiscardLoading}
                >
                  {bulkDiscardLoading ? t('common.loading') : t('common.confirm')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {isSchedule ? (
        context === 'gym' && gymSlug ? (
          <SchedulePanel
            gymSlug={gymSlug}
            canManage={!!isStaffMember}
            isAdmin={!!isStaffAdmin}
            initialDate={filters.date}
            onDateChange={(date) => updateFilters({ date })}
          />
        ) : null
      ) : isBoard && selectedBoard && gymSlug ? (
        <BoardTabContent
          board={selectedBoard}
          gymSlug={gymSlug}
          isStaffAdmin={!!isStaffAdmin}
          loggedIn={!!user}
          filterKey={filterKey}
          returnPath={returnPath}
          saveFilteredClimbs={saveFilteredClimbs}
          newSince={newSince}
        />
      ) : isBoard ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">{t('climbs.boardEmpty')}</p>
          </CardContent>
        </Card>
      ) : (
      <Card>
        {/* One list for the rows' swipe strips, so opening one closes the rest. */}
        <SwipeRoot render={<CardContent className="p-0" />}>
          {currentFilteredClimbs.length > 0 ? (
            groupedClimbs ? (
              groupedClimbs.map((group) => (
                <div key={group.label}>
                  <div className="sticky top-0 z-10 flex items-center gap-2 px-4 py-2 bg-muted/60 border-b text-xs font-medium text-muted-foreground">
                    <span>{group.label}</span>
                    <span className="text-muted-foreground/60">({group.climbs.length})</span>
                  </div>
                  {group.climbs.map((climb) => (
                    <div
                      key={climb.id}
                      ref={climb.slug === highlightedClimbSlug ? scrollToHighlighted : undefined}
                      className={`transition-[opacity,background-color] duration-300 ${deletingClimbIds.has(climb.id) ? 'opacity-0' : ''} ${climb.slug === highlightedClimbSlug ? 'bg-primary/5' : ''}`}
                    >
                      <ClimbRow
                        climb={climb}
                        isStaffAdmin={isStaffAdmin}
                        onClimbClick={handleClimbClick}
                        onClimbUpdated={handleClimbUpdated}
                        onClimbDeleted={handleClimbDeleted}
                        onLongPress={handleLongPress}
                        isNew={isNew(climb)}
                      />
                    </div>
                  ))}
                </div>
              ))
            ) : (
              currentFilteredClimbs.map((climb) => (
                <div
                  key={climb.id}
                  ref={climb.slug === highlightedClimbSlug ? scrollToHighlighted : undefined}
                  className={`transition-[opacity,background-color] duration-300 ${deletingClimbIds.has(climb.id) ? 'opacity-0' : ''} ${climb.slug === highlightedClimbSlug ? 'bg-primary/5' : ''}`}
                >
                  <ClimbRow
                    climb={climb}
                    isStaffAdmin={isStaffAdmin}
                    onClimbClick={handleClimbClick}
                    onClimbUpdated={handleClimbUpdated}
                    onClimbDeleted={handleClimbDeleted}
                    onLongPress={handleLongPress}
                    isNew={isNew(climb)}
                  />
                </div>
              ))
            )
          ) : (
            <div className="p-12 text-center">
              <p className="text-muted-foreground mb-4">
                {climbTypeTab === 'boulders' ? t('climbs.noBoulders') : t('climbs.noRoutes')}
              </p>
              {context === 'gym' && gymSlug && (
                <button
                  onClick={() => {
                    const climbType = climbTypeTab;
                    trackEvent('climb_create_started', { climb_type: climbType });
                    navigate(`/${locationType === 'competition' ? 'competitions' : 'gyms'}/${gymSlug}/${climbType}/new`);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  {climbTypeTab === 'boulders' ? t('climbs.createFirstBoulder') : t('climbs.createFirstRoute')}
                </button>
              )}
            </div>
          )}
        </SwipeRoot>
      </Card>
      )}

      {lightboxIndex !== null && (
        <ClimbImageLightbox
          climbs={currentFilteredClimbs}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(climb) => { setLightboxIndex(null); handleClimbClick(climb); }}
          userAuthenticated={!!user}
          adminActions={user ? (climb) => (isStaffAdmin || isClimbAuthor(climb, user)) ? (
            <ClimbAdminActions
              climb={climb}
              onClimbUpdated={handleClimbUpdated}
              onClimbDeleted={handleClimbDeleted}
              showAge={false}
              canReview={!!isStaffAdmin}
            />
          ) : null : undefined}
        />
      )}

      {context === 'gym' && gymSlug && isClimbList && (
        <ClimbsListFab
          activeTab={climbTypeTab}
          isStaffMember={!!isStaffMember}
          onAddClimb={() => {
            const climbType = climbTypeTab;
            trackEvent('climb_create_started', { climb_type: climbType });
            navigate(`/${locationType === 'competition' ? 'competitions' : 'gyms'}/${gymSlug}/${climbType}/new`);
          }}
          onScan={() => setShowScanDialog(true)}
          onAddAnnouncement={onAddAnnouncement}
        />
      )}
    </div>
  );
}
